import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { AlertCircle, ArrowRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useApproveLisResult, useLisReviewDetail, useRejectLisResult } from '@/hooks/useLisReview'
import { usePermission } from '@/lib/permissions'
import {
  approveBlockedReason,
  CHANGE_LABEL,
  formatReviewValue,
  groupFailures,
  LIS_PENDING_OUTLINE_CLASS,
  lisFormLink,
  resultStatusLabel,
  writesValue,
} from '@/lib/lisReview'
import { cn } from '@/lib/utils'

/**
 * The LIMS data for one received result, and the decision on it. Nothing here
 * is written to a form until Approve; Reject keeps the result as a record.
 */
export function LisReviewDialog({
  resultId,
  onClose,
  onDecided,
  currentSubmissionId,
  onNavigate,
}: {
  resultId: string | null
  onClose: () => void
  /** Called after a successful approve, so the page can reload its forms. */
  onDecided?: (outcome: 'approved' | 'rejected') => void
  /** The form open behind the dialog, if any: no "Go to form" link to itself. */
  currentSubmissionId?: string | null
  /** Called when the reviewer follows the link to the form. */
  onNavigate?: () => void
}) {
  const { pathname } = useLocation()
  const { can } = usePermission()
  const canEdit = can('submissions.edit')
  const { data: detail, isLoading, error } = useLisReviewDetail(resultId)
  const approve = useApproveLisResult()
  const reject = useRejectLisResult()
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const busy = approve.isPending || reject.isPending

  function close() {
    if (busy) return
    setRejecting(false)
    setReason('')
    setActionError(null)
    onClose()
  }

  async function doApprove() {
    if (!resultId) return
    setActionError(null)
    try {
      const res = await approve.mutateAsync(resultId)
      onDecided?.('approved')
      // Part of it could not be placed: stay open so the reviewer sees what.
      if (res.failures.length > 0) {
        setActionError(
          `Approved: ${res.applied} value${res.applied === 1 ? '' : 's'} written. Not written: ${res.failures
            .map((f) => f.detail)
            .join('; ')}`,
        )
        return
      }
      close()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not approve this result.')
    }
  }

  async function doReject() {
    if (!resultId) return
    setActionError(null)
    try {
      await reject.mutateAsync({ id: resultId, reason: reason.trim() || undefined })
      onDecided?.('rejected')
      close()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not reject this result.')
    }
  }

  const blocked = detail ? approveBlockedReason(detail, canEdit) : null
  const pending = detail?.status === 'PENDING'
  const label = detail ? (detail.testName ? `${detail.testCode} · ${detail.testName}` : detail.testCode) : ''
  const formLink = detail ? lisFormLink(detail) : null
  // Already there: the transplant page is open on this form (or, when no form
  // is known, on this transplant at all).
  const onFormAlready =
    !!formLink &&
    pathname === formLink.href.split('?')[0] &&
    (formLink.submissionId === null || formLink.submissionId === currentSubmissionId)

  return (
    <Dialog open={!!resultId} onOpenChange={(o) => !o && close()}>
      <DialogContent className='sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>LIS result{label ? `: ${label}` : ''}</DialogTitle>
          <DialogDescription>
            Review the laboratory data before it is written to the form. Nothing is saved until you
            approve.
          </DialogDescription>
        </DialogHeader>

        {(error || actionError) && (
          <div className='flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {actionError ?? (error instanceof Error ? error.message : 'Could not load this result.')}
          </div>
        )}

        {isLoading || !detail ? (
          <div className='flex items-center justify-center py-10'>
            {!error && <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />}
          </div>
        ) : (
          <div className='max-h-[60vh] space-y-4 overflow-y-auto'>
            <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground'>
              {detail.mrn && <span>MRN {detail.mrn}</span>}
              {detail.orderId && <span>Order {detail.orderId}</span>}
              {resultStatusLabel(detail.resultStatus) && (
                <span>{resultStatusLabel(detail.resultStatus)}</span>
              )}
              {detail.notDirectlyOrdered && <span>Not directly ordered (panel or reflex)</span>}
              <span
                className={cn(
                  'rounded-full border px-2 py-0.5 font-medium',
                  pending ? LIS_PENDING_OUTLINE_CLASS : 'border-border',
                )}
              >
                {detail.status === 'PENDING' ? 'Waiting for review' : detail.status.replace('_', ' ').toLowerCase()}
              </span>
              {detail.reviewedBy && <span>by {detail.reviewedBy}</span>}
              {formLink && !onFormAlready && (
                <Link
                  to={formLink.href}
                  onClick={onNavigate}
                  className='inline-flex items-center gap-1 font-medium text-primary hover:underline'
                >
                  {formLink.label}
                  <ArrowRight className='h-3 w-3' />
                </Link>
              )}
            </div>
            {detail.reviewNote && (
              <p className='text-sm text-muted-foreground'>Reason: {detail.reviewNote}</p>
            )}

            <section>
              <h3 className='mb-1.5 text-sm font-semibold'>Values to write to the form</h3>
              {detail.proposals.length === 0 ? (
                <p className='text-sm text-muted-foreground'>
                  No mapped form fields for this result. Check the mapping on the LIS integration.
                </p>
              ) : (
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='border-b text-left text-xs text-muted-foreground'>
                      <th className='py-1.5 pr-3 font-medium'>Field</th>
                      <th className='py-1.5 pr-3 font-medium'>Current</th>
                      <th className='py-1.5 pr-3 font-medium'>Proposed</th>
                      <th className='py-1.5 font-medium'>Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.proposals.map((p, i) => (
                      <tr
                        key={`${p.formDefinitionId}:${p.targetFieldId}:${i}`}
                        className={cn('border-b last:border-0 align-top', !writesValue(p.change) && 'text-muted-foreground')}
                      >
                        <td className='py-1.5 pr-3'>
                          <div className='font-medium text-foreground'>{p.fieldLabel}</div>
                          <div className='text-xs'>
                            {[p.formName, p.sourceLabel && `from ${p.sourceLabel}`].filter(Boolean).join(' · ')}
                          </div>
                        </td>
                        <td className='py-1.5 pr-3'>{formatReviewValue(p.currentValue)}</td>
                        <td className='py-1.5 pr-3 text-foreground'>{formatReviewValue(p.proposedValue)}</td>
                        <td className='py-1.5'>
                          <div>{CHANGE_LABEL[p.change]}</div>
                          {p.change === 'blocked' && p.blockedDetail && (
                            <div className='text-xs text-amber-700'>{p.blockedDetail}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {detail.failures && detail.failures.length > 0 && (
              <ul className='space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
                {groupFailures(detail.failures).map((f) => (
                  <li key={f.detail}>
                    {f.detail}
                    {f.count > 1 && <span className='ml-1 font-medium'>({f.count} values)</span>}
                  </li>
                ))}
              </ul>
            )}

            <section>
              <h3 className='mb-1.5 text-sm font-semibold'>Received data</h3>
              {detail.observations.length === 0 ? (
                <p className='text-sm text-muted-foreground'>No observations.</p>
              ) : (
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='border-b text-left text-xs text-muted-foreground'>
                      <th className='py-1.5 pr-3 font-medium'>Observation</th>
                      <th className='py-1.5 pr-3 font-medium'>Value</th>
                      <th className='py-1.5 pr-3 font-medium'>Units</th>
                      <th className='py-1.5 pr-3 font-medium'>Reference</th>
                      <th className='py-1.5 font-medium'>Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.observations.map((o, i) => (
                      <tr key={`${o.code}:${i}`} className='border-b last:border-0'>
                        <td className='py-1.5 pr-3'>{o.codeText ?? o.code}</td>
                        <td className='py-1.5 pr-3'>{formatReviewValue(o.value)}</td>
                        <td className='py-1.5 pr-3'>{formatReviewValue(o.units)}</td>
                        <td className='py-1.5 pr-3'>{formatReviewValue(o.referenceRange)}</td>
                        <td className='py-1.5'>{formatReviewValue(o.abnormalFlag)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {rejecting && (
              <div className='space-y-1'>
                <label htmlFor='lis-reject-reason' className='text-sm font-medium'>
                  Reason (optional)
                </label>
                <Textarea
                  id='lis-reject-reason'
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder='Why is this result not being filed?'
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={close} disabled={busy}>
            Close
          </Button>
          {detail && pending && canEdit && !rejecting && (
            <Button variant='outline' onClick={() => setRejecting(true)} disabled={busy}>
              Reject
            </Button>
          )}
          {rejecting && (
            <>
              <Button variant='outline' onClick={() => setRejecting(false)} disabled={busy}>
                Back
              </Button>
              <Button variant='destructive' onClick={doReject} disabled={busy}>
                {reject.isPending && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
                Confirm reject
              </Button>
            </>
          )}
          {detail && !rejecting && (
            <span title={blocked ?? undefined}>
              <Button onClick={doApprove} disabled={busy || blocked !== null}>
                {approve.isPending && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
                Approve
              </Button>
            </span>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
